// frontend/modules/customer-notification/views/NotificationTriggerView.tsx
import React from 'react';
import { Card, Typography, Button, Space, Row, Col } from 'antd';
import { BellOutlined, GiftOutlined, EditOutlined, ApiOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useNotificationTrigger } from '../hooks/useNotificationTrigger';
import ManualNotificationTrigger from '../components/trigger/ManualNotificationTrigger';
import MarketingNotificationTrigger from '../components/trigger/MarketingNotificationTrigger';
import CustomNotificationTrigger from '../components/trigger/CustomNotificationTrigger';

const { Title, Text } = Typography;

const NotificationTriggerView: React.FC = () => {
    const { dark } = useTheme();
    const {
        activeFormType,
        showTriggerForm,
        hideTriggerForm
    } = useNotificationTrigger();

    // Render active form based on type
    const renderActiveForm = () => {
        switch (activeFormType) {
            case 'manual':
                return <ManualNotificationTrigger onClose={hideTriggerForm} />;

            case 'marketing':
                return <MarketingNotificationTrigger onClose={hideTriggerForm} />;

            case 'custom':
                return <CustomNotificationTrigger onClose={hideTriggerForm} />;

            default:
                return null;
        }
    };

    // If a form is active, display it
    if (activeFormType) {
        return (
            <div className="space-y-6">
                <Card
                    className={dark ? 'bg-gray-800 text-white' : ''}
                    title={
                        <div className="flex justify-between items-center">
                            <Title level={4} className={dark ? 'text-white mb-0' : 'mb-0'}>
                                <Space>
                                    <BellOutlined />
                                    <span>Send Notifications</span>
                                </Space>
                            </Title>
                            <Button onClick={hideTriggerForm}>
                                Back to Options
                            </Button>
                        </div>
                    }
                >
                    {renderActiveForm()}
                </Card>
            </div>
        );
    }

    // Otherwise, show the options to select a notification type
    return (
        <div className="space-y-6">
            <Card
                className={dark ? 'bg-gray-800 text-white' : ''}
                title={
                    <div className="flex justify-between items-center">
                        <Title level={4} className={dark ? 'text-white mb-0' : 'mb-0'}>
                            <Space>
                                <BellOutlined />
                                <span>Send Notifications</span>
                            </Space>
                        </Title>
                    </div>
                }
            >
                <Text className="block mb-6">
                    Select the type of notification you want to send to customers:
                </Text>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <Card
                            hoverable
                            onClick={() => showTriggerForm('manual')}
                            className="h-full flex flex-col"
                        >
                            <div className="text-center mb-4">
                                <ApiOutlined style={{ fontSize: 36, color: '#1890ff' }} />
                            </div>
                            <Title level={5} className="text-center mb-2">
                                Manual Trigger
                            </Title>
                            <Text className="text-center block">
                                Execute any notification process to send targeted notifications based on custom criteria.
                            </Text>
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Card
                            hoverable
                            onClick={() => showTriggerForm('marketing')}
                            className="h-full flex flex-col"
                        >
                            <div className="text-center mb-4">
                                <GiftOutlined style={{ fontSize: 36, color: '#52c41a' }} />
                            </div>
                            <Title level={5} className="text-center mb-2">
                                Marketing & Announcements
                            </Title>
                            <Text className="text-center block">
                                Send promotional messages, product changes, meeting invitations, and other announcements.
                            </Text>
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Card
                            hoverable
                            onClick={() => showTriggerForm('custom')}
                            className="h-full flex flex-col"
                        >
                            <div className="text-center mb-4">
                                <EditOutlined style={{ fontSize: 36, color: '#722ed1' }} />
                            </div>
                            <Title level={5} className="text-center mb-2">
                                Custom Notification
                            </Title>
                            <Text className="text-center block">
                                Send custom or template-based messages to selected customers across different notification platforms.
                            </Text>
                        </Card>
                    </Col>
                </Row>

                <div className="mt-8 bg-gray-100 p-4 rounded">
                    <Title level={5}>
                        <Space>
                            <ClockCircleOutlined />
                            <span>Recent Activity</span>
                        </Space>
                    </Title>
                    <Text className="block">
                        View your <a href="/notifications/history">notification history</a> to see recently sent messages or check the <a href="/notifications/dashboard">dashboard</a> for notification analytics.
                    </Text>
                </div>
            </Card>
        </div>
    );
};

export default NotificationTriggerView;